import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Divider
} from '@mui/material'
import React, { useMemo, useState } from 'react'

export interface LocationBin {
  id: string
  binnumber: string
  location: string
}

interface TransferBinFormProps {
  fromBin: string
  locationBins: LocationBin[]
  onBack: () => void
  onSubmit: (quantity: number, toBin: string, memo?: string) => void
  onSetPreferred: () => void
  loading: boolean
}

const TransferBinForm: React.FC<TransferBinFormProps> = ({
  fromBin,
  locationBins,
  onBack,
  onSubmit,
  onSetPreferred,
  loading
}) => {
  const [quantity, setQuantity] = useState<string>('')
  const [toBin, setToBin] = useState<string>('')
  const [memo, setMemo] = useState<string>('')
  const [errors, setErrors] = useState<{ quantity?: string; toBin?: string }>(
    {}
  )

  const targetBinOptions = useMemo(
    () => (locationBins ?? []).filter(bin => bin.binnumber !== fromBin),
    [locationBins, fromBin]
  )
  const selectedBin = useMemo(
    () => targetBinOptions.find(b => b.binnumber === toBin) ?? null,
    [targetBinOptions, toBin]
  )

  const handleQuantityChange = (value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setQuantity(value)
      if (errors.quantity) {
        setErrors(prev => ({ ...prev, quantity: undefined }))
      }
    }
  }

  const handleToBinChange = (_: unknown, value: LocationBin | null) => {
    setToBin(value?.binnumber ?? '')
    if (errors.toBin) {
      setErrors(prev => ({ ...prev, toBin: undefined }))
    }
  }

  const handleSubmit = () => {
    const newErrors: { quantity?: string; toBin?: string } = {}

    if (!quantity.trim()) {
      newErrors.quantity = 'Please enter quantity'
    } else {
      const numQuantity = parseFloat(quantity)
      if (isNaN(numQuantity) || numQuantity <= 0) {
        newErrors.quantity = 'Quantity must be greater than 0'
      }
    }

    if (!toBin) {
      newErrors.toBin = 'Please select target bin'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    onSubmit(parseFloat(quantity), toBin, memo.trim() || undefined)
  }

  return (
    <>
      <Box sx={{ margin: '20px' }}>
        <Button variant='outlined' onClick={onBack} disabled={loading}>
          Back
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          padding: '20px'
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px'
          }}
        >
          <CardContent sx={{ padding: '32px' }}>
            <Typography
              variant='h5'
              sx={{
                textAlign: 'center',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#364c63'
              }}
            >
              Transfer Bin
            </Typography>
            <Divider sx={{ marginBottom: '24px' }} />

            <Box
              sx={{
                backgroundColor: '#f5f7fa',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: '#6b7280', marginBottom: '4px' }}
              >
                Current Bin
              </Typography>
              <Typography
                variant='h6'
                sx={{ color: '#364c63', fontWeight: 600 }}
              >
                {fromBin}
              </Typography>
            </Box>

            <Box sx={{ marginBottom: '24px' }}>
              <Typography
                variant='body1'
                sx={{ marginBottom: '8px', fontWeight: 500, color: '#374151' }}
              >
                Quantity to Transfer <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type='text'
                value={quantity}
                onChange={e => handleQuantityChange(e.target.value)}
                placeholder='Enter quantity'
                error={!!errors.quantity}
                helperText={errors.quantity}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#364c63'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#364c63'
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ marginBottom: '24px' }}>
              <Typography
                variant='body1'
                sx={{ marginBottom: '8px', fontWeight: 500, color: '#374151' }}
              >
                Memo
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={memo}
                onChange={e => setMemo(e.target.value)}
                placeholder='Optional memo for NetSuite (e.g. reason for transfer)'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#364c63'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#364c63'
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ marginBottom: '32px' }}>
              <Typography
                variant='body1'
                sx={{ marginBottom: '8px', fontWeight: 500, color: '#374151' }}
              >
                Target Bin <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Autocomplete
                value={selectedBin}
                onChange={handleToBinChange}
                options={targetBinOptions}
                getOptionLabel={option => option.binnumber}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Search or select target bin'
                    error={!!errors.toBin}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': {
                          borderColor: '#364c63'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#364c63'
                        }
                      }
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.binnumber === value.binnumber
                }
              />
              {errors.toBin && (
                <Typography
                  variant='caption'
                  sx={{
                    color: '#ef4444',
                    marginTop: '4px',
                    marginLeft: '14px'
                  }}
                >
                  {errors.toBin}
                </Typography>
              )}
            </Box>

            <Button
              variant='contained'
              fullWidth
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                background: 'linear-gradient(45deg, #364c63, #5f799a)',
                color: 'white',
                fontWeight: 600,
                padding: '14px 0',
                fontSize: '16px',
                textTransform: 'none',
                borderRadius: '8px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4b6485, #758aa8)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(54, 76, 99, 0.3)'
                },
                '&:disabled': {
                  background: '#9ca3af',
                  transform: 'none'
                }
              }}
            >
              {loading ? 'Processing...' : 'Confirm Transfer'}
            </Button>
            <Button
              variant='outlined'
              fullWidth
              onClick={onSetPreferred}
              disabled={loading}
              sx={{
                marginTop: '16px',
                color: '#364c63',
                borderColor: '#364c63',
                fontWeight: 600,
                padding: '14px 0',
                fontSize: '16px',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: '#456280',
                  backgroundColor: 'rgba(54, 76, 99, 0.04)'
                }
              }}
            >
              Set as Preferred Bin
            </Button>
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

export default TransferBinForm
